import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Gauge, RefreshCw } from 'lucide-react';
import { einschaetzungAPI } from '@/lib/api';

/* Pipeline-Ansicht mit Ampeln (v5, M1): juengste Einschaetzung je Lead,
   sortierbar nach Score, Filter "rot ohne offene Aufgabe". */

const AMPEL = { gruen: 'bg-green-500', gelb: 'bg-amber-400', rot: 'bg-red-500' };

export default function EinschaetzungenPage() {
  const [daten, setDaten] = useState({ zeilen: [], ampeln: {} });
  const [filter, setFilter] = useState('');
  const [sortierung, setSortierung] = useState('score');
  const [laden, setLaden] = useState(true);

  const lade = useCallback(async () => {
    setLaden(true);
    try { const r = await einschaetzungAPI.uebersicht({ filter: filter || undefined, sortierung }); setDaten(r.data); }
    catch (e) { /* leer lassen */ }
    finally { setLaden(false); }
  }, [filter, sortierung]);
  useEffect(() => { lade(); }, [lade]);

  return (
    <div className="p-6 space-y-4" data-testid="einschaetzungen-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Gauge className="w-5 h-5" /> Einschätzungen</h1>
        <div className="flex items-center gap-2 text-xs">
          {['rot', 'gelb', 'gruen'].map((a) => (
            <span key={a} className="flex items-center gap-1"><span className={`inline-block h-2.5 w-2.5 rounded-full ${AMPEL[a]}`} />{daten.ampeln?.[a] ?? 0}</span>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setFilter(filter ? '' : 'rot_ohne_aufgabe')}
                  className={`rounded-full px-3 py-1 text-xs border ${filter ? 'bg-red-600 text-white border-red-600' : 'border-border text-muted-foreground'}`}
                  data-testid="filter-rot-ohne-aufgabe">Rot ohne offene Aufgabe</button>
          <select value={sortierung} onChange={(e) => setSortierung(e.target.value)} className="text-xs border rounded-md px-2 py-1 bg-background">
            <option value="score">Nach Score (niedrig zuerst)</option>
            <option value="zeit">Nach Zeit (neueste zuerst)</option>
          </select>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={lade}><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
      </div>

      {laden ? <p className="text-sm text-muted-foreground">Lade …</p> : daten.zeilen.length === 0 ? (
        <p className="text-sm text-muted-foreground">Keine Einschätzungen{filter ? ' für diesen Filter' : ''}. Einschätzungen entstehen über „Neu einschätzen" am Lead oder automatisch, sobald EINSCHAETZUNG_AUTOMATIK gesetzt ist.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border/60">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr><th className="text-left p-2">Ampel</th><th className="text-left p-2">Lead</th><th className="text-left p-2">Stufe</th><th className="text-right p-2">Score</th><th className="text-left p-2">Nächste Aktion</th><th className="text-left p-2">Aufgabe</th><th className="text-left p-2">Stand</th></tr>
            </thead>
            <tbody>
              {daten.zeilen.map((z) => (
                <tr key={z.id} className="border-t border-border/40 hover:bg-muted/30" data-testid={`einschaetzung-zeile-${z.lead_id}`}>
                  <td className="p-2"><span className={`inline-block h-3 w-3 rounded-full ${AMPEL[z.ampel] || 'bg-slate-400'}`} /></td>
                  <td className="p-2"><Link to={`/leads/${z.lead_id}`} className="font-medium hover:underline">{z.lead?.name || z.lead_id}</Link></td>
                  <td className="p-2"><Badge variant="secondary">{z.lead?.stage}</Badge></td>
                  <td className="p-2 text-right tabular-nums">{z.score}</td>
                  <td className="p-2 max-w-md truncate" title={z.naechste_aktion?.text || ''}>{z.naechste_aktion?.typ}{z.naechste_aktion?.begruendung ? ` — ${z.naechste_aktion.begruendung}` : ''}</td>
                  <td className="p-2 text-xs">{z.offene_aufgabe ? 'offen' : <span className="text-red-600">keine</span>}</td>
                  <td className="p-2 text-xs text-muted-foreground">{new Date(z.erstellt_am).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
